import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { articleApiClient } from '@src/api/tenant/article/articleApiClient';
import { mediaCategoryApiClient } from '@src/api/tenant/mediaCategory/mediaCategoryApiClient';
import { mediaTagApiClient } from '@src/api/tenant/mediaTag/mediaTagApiClient';
import { ArticleForm, ArticleFormError } from '@src/api/types/article';
import { MediaCategory } from '@src/api/types/mediaCategory';
import { MediaTag } from '@src/api/types/mediaTag';
import IconImage from '@src/assets/images/common/icon/chat-action/icon-image.svg';
import { useEditorSidebar } from '@src/context/EditorSidebarContext';
import { ArticleStatus } from '@src/enum/articleStatus';
import { PublicationStatus } from '@src/enum/publicationStatus';
import { useToast } from '@src/features/toast/context/toastContext';
import { useUnsavedChanges } from '@src/hooks/useUnsavedChange';
import TOAST_MESSAGE from '@src/modules/constants/toastMessage';
import escapeHtml from '@src/modules/utils/escapeHtml';
import { TENANT_URL } from '@src/routes/tenant';
import { TiptapEditorHandle } from '@src/ui/Editor';
import EditorHeader from '@src/ui/elements/form/EditorHeader';
import EditorSidebar from '@src/ui/templates/EditorSidebar';
import ConfirmLeaveModal from '@src/ui/templates/Shared/ConfirmLeaveModal';
import ConfirmModal from '@src/ui/templates/Shared/ConfirmModal';
import { TenantTiptapEditor } from '@src/ui/templates/Tenant/TenantTiptapEditor/TenantTiptapEditor';
import {
  convertIframeTagToSafeIframeAttr,
  convertStyleAttrToSafeStyleAttr,
  processImagesAndResize,
} from '@src/utils/editFormUtils';
import logger from '@src/utils/logger.ts';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import SettingForm from './components/SettingSideBar/SettingForm';

const ArticleEditorForm = () => {
  const [errors, setErrors] = useState<ArticleFormError>({} as ArticleFormError);
  const [categoryOptions, setCategoryOptions] = useState<MediaCategory[]>([]);
  const [availableTags, setAvailableTags] = useState<MediaTag[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const oldContentRef = useRef('');
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams();
  const IS_EDIT_MODE = id !== undefined;
  const methods = useForm<ArticleForm>({
    defaultValues: {
      title: '',
      published_at: '',
      tags: [],
      category: {},
      description: '',
      content: '',
      thumbnail_url: '',
      pdf_url: '',
      original_pdf_name: '',
      status: '',
      related_articles: [],
      media_category_id: '',
      media_tags: [],
      is_pickup: false,
      is_top_display: false,
      is_information: false,
    },
  });
  const dragCounter = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const { isOpen: isOpenSideBar, setIsOpen: setIsOpenSidebar } = useEditorSidebar();

  const contentWidth = isOpenSideBar ? 'w-[calc(100%-19rem)]' : 'w-[calc(100%-68px)]';

  const stripTags = (input: string): string => {
    return input.replace(/<(?!img\b)[^>]+>/g, '');
  };

  const openPreview = async () => {
    const { ...data } = methods.getValues();
    let editorContent = await processImagesAndResize(
      oldContentRef.current,
      data.content,
      uploadFileFn,
      deleteFileFn,
    );

    editorContent = convertStyleAttrToSafeStyleAttr(editorContent);
    editorContent = convertIframeTagToSafeIframeAttr(editorContent);

    if (stripTags(editorContent).trim() === '' && !editorContent.includes('data-iframe-original')) {
      editorContent = '';
    }
    const formData = {
      ...data,
      content: escapeHtml(editorContent),
      related_articles: data.related_articles.map((article) =>
        typeof article === 'number' ? article : article.id,
      ),
      // 登録画面の場合は強制的に下書き
      status: id ? data.status : ArticleStatus.DRAFT,
    };

    if (id) {
      const previewRes = await articleApiClient.preview(+id, formData);
      handleApiResponse(
        previewRes,
        (data) => {
          const uuid = data.uuid;
          if (!uuid) {
            return;
          }
          const url = generatePath(TENANT_URL.ARTICLES_PREVIEW, { uuid: uuid });
          window.open(url, '_blank', 'noopener,noreferrer');
        },
        (errors) => {
          setErrors(errors);
        },
      );
    } else {
      // 登録画面の場合、新規保存後のIDを利用してプレビューテーブルを作成して遷移
      const createResponse = await articleApiClient.create(formData);
      handleApiResponse(
        createResponse,
        async (data) => {
          const previewRes = await articleApiClient.preview(data.id, formData);
          handleApiResponse(
            previewRes,
            (data) => {
              const url = generatePath(TENANT_URL.ARTICLES_PREVIEW, { uuid: data.uuid });
              window.open(url, '_blank', 'noopener,noreferrer');
            },
            (errors) => {
              setErrors(errors);
            },
          );
          navigate(generatePath(TENANT_URL.ARTICLES_EDIT, { id: data.id }));
        },
        (errors) => {
          setErrors(errors);
        },
      );
    }
  };

  const fetchDetail = async () => {
    if (!id) {
      return;
    }
    const response = await articleApiClient.show(+id);
    handleApiResponse(response, (data) => {
      const formattedEventData = {
        ...data,
        media_category_id: data.category?.id?.toString(),
        media_tags: data?.tags?.map((tag) => (typeof tag === 'number' ? tag : tag.id)),
      } as ArticleForm;
      methods.reset(formattedEventData);
      oldContentRef.current = formattedEventData.content;
    });
  };

  const uploadFileFn = async (file: File) => {
    let uploadedUrl = '';
    const res = await articleApiClient.uploadThumbnail(file);
    handleApiResponse(
      res,
      (data) => {
        uploadedUrl = data.url ?? '';
      },
      (errors) => {
        toast('アップロードに失敗しました。');
        console.error('アップロード失敗', errors);
      },
    );
    setIsDragging(false);
    return uploadedUrl;
  };
  const deleteFileFn = async (url: string) => {
    const res = await articleApiClient.deleteFile(url);
    handleApiResponse(
      res,
      () => {
        logger.log('ファイルを削除しました。');
      },
      (errors) => {
        console.error('削除失敗', errors);
      },
    );
  };

  const handleSave = async (status: PublicationStatus) => {
    const { ...data } = methods.getValues();
    let editorContent = await processImagesAndResize(
      oldContentRef.current,
      data.content,
      uploadFileFn,
      deleteFileFn,
    );

    editorContent = convertStyleAttrToSafeStyleAttr(editorContent);
    editorContent = convertIframeTagToSafeIframeAttr(editorContent);

    if (stripTags(editorContent).trim() === '' && !editorContent.includes('data-iframe-original')) {
      editorContent = '';
    }
    const formData = {
      ...data,
      content: escapeHtml(editorContent),
      related_articles: data.related_articles.map((article) =>
        typeof article === 'number' ? article : article.id,
      ),
      status: status.toString(),
    };

    setLoading(true);
    const response = IS_EDIT_MODE
      ? await articleApiClient.update(+id, formData)
      : await articleApiClient.create(formData);
    handleApiResponse(
      response,
      async (data) => {
        setLoading(false);
        toast(
          IS_EDIT_MODE
            ? status === PublicationStatus.DRAFT
              ? TOAST_MESSAGE.UPDATE
              : TOAST_MESSAGE.REGISTER
            : status === PublicationStatus.DRAFT
              ? TOAST_MESSAGE.SAVE_DRAFT
              : TOAST_MESSAGE.REGISTER,
        );
        await articleApiClient.preview(data.id, formData);
        navigate(TENANT_URL.ARTICLES);
      },
      (errors) => {
        setLoading(false);
        setIsOpenSidebar(true);
        setErrors(errors);
      },
    );
    setLoading(false);
    oldContentRef.current = editorContent;
  };

  const handleDelete = async () => {
    if (!id) {
      return;
    }
    setLoading(true);
    const response = await articleApiClient.delete(+id);
    handleApiResponse(response, () => {
      setLoading(false);
      toast(TOAST_MESSAGE.DELETE);
      navigate(TENANT_URL.ARTICLES);
    });
    setLoading(false);
  };

  const loadCategories = async () => {
    const response = await mediaCategoryApiClient.index();
    handleApiResponse(response, (data) => {
      setCategoryOptions(data);
    });
  };

  const loadTags = async () => {
    const response = await mediaTagApiClient.suggest();
    handleApiResponse(response, (data) => {
      setAvailableTags(data);
    });
  };

  useEffect(() => {
    if (IS_EDIT_MODE) {
      fetchDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [IS_EDIT_MODE]);

  useEffect(() => {
    loadCategories();
    loadTags();
  }, []);

  // フォーカスをエディターに設定
  useEffect(() => {
    const timer = setTimeout(() => {
      const editor = editorRef.current?.getEditor();
      if (editor) {
        editor.commands.focus();
      }
    }, 100); // エディターの初期化を待つための短い遅延

    return () => clearTimeout(timer);
  }, []);

  const openDeleteModal = () => {
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    setIsDeleteModalOpen(false);
    handleDelete();
  };

  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
  };

  const handleEditorChange = (content: string) => {
    methods.setValue('content', content);
  };

  const hasUnsavedChanges =
    !!methods.getValues('title') ||
    !!methods.getValues('content') ||
    !!methods.getValues('media_category_id') ||
    !!methods.getValues('media_tags')?.length;

  const { isModalOpen, handleReturn, cancelReturn, setIsModalOpen } = useUnsavedChanges({
    hasUnsavedChanges,
    onConfirm: () => {
      navigate(TENANT_URL.ARTICLES);
    },
  });

  const handleOnConfirm = () => {
    setIsModalOpen(false);
    navigate(TENANT_URL.ARTICLES);
  };

  const handleDragEnter: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    dragCounter.current += 1;
    setIsDragging(true);
  };

  const handleDragLeave: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
  };

  const editorRef = useRef<TiptapEditorHandle | null>(null);
  const handleDrop: React.DragEventHandler<HTMLDivElement> = async (e) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragging(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      const imageFiles = Array.from(files).filter((file) => file.type.startsWith('image/'));

      for (const file of imageFiles) {
        const uploadedUrl = await uploadFileFn(file);
        const editor = editorRef.current?.getEditor();
        if (editor) {
          const position = editor.state.selection?.$from;

          editor
            .chain()
            .setTextSelection(position?.pos ?? editor.state.doc.content.size)
            .insertContent(`<img src="${uploadedUrl}" alt="${file.name}" />`)
            .run();
        }
      }
    }
  };

  return (
    <div
      className='editor-wrapper relative flex flex-col'
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <main className='flex grow flex-col'>
        <EditorHeader
          onReturn={handleReturn}
          onPreview={openPreview}
          onSaveDraft={() => handleSave(PublicationStatus.DRAFT)}
          onPublish={() => handleSave(PublicationStatus.PUBLISHED)}
          onDelete={openDeleteModal}
          errors={errors}
          isCreateForm={!IS_EDIT_MODE}
          isDraft={methods.getValues('status') == PublicationStatus.DRAFT.toString()}
          loading={loading}
        />
        <div className='flex max-h-[calc(100vh-143px)] grow'>
          <div className='flex shrink-0 flex-col overflow-hidden overflow-y-auto border-r-gray-200 bg-gray-50'>
            <EditorSidebar>
              <SettingForm
                categoryOptions={categoryOptions}
                availableTags={availableTags}
                setAvailableTags={setAvailableTags}
                errors={errors}
                setErrors={setErrors}
                methods={methods}
              />
            </EditorSidebar>
          </div>
          <div className={`flex grow flex-col ${contentWidth}`}>
            <TenantTiptapEditor
              value={methods.getValues('content')}
              onChange={handleEditorChange}
              enabled={{
                richText: true,
                mentions: false,
                images: true,
                links: true,
                tables: true,
                iframes: true,
                textStyles: true,
                lists: true,
                codeBlocks: false,
                quotes: false,
                dividers: false,
                history: true,
                dragDrop: true,
                fileUpload: true,
                fileSave: false,
                shortcut: {
                  underline: true,
                  strike: false,
                  heading: true,
                  textAlign: true,
                  blockquote: false,
                },
              }}
              height='100%'
              width='100%'
              uploadFunction={uploadFileFn}
              ref={editorRef}
            />
          </div>
          <ConfirmLeaveModal
            isOpen={isModalOpen}
            onConfirm={handleOnConfirm}
            onClose={cancelReturn}
          />
          <ConfirmModal
            title='完全に削除しますか？'
            message='記事が完全に削除されますがよろしいですか？'
            confirmText='完全に削除'
            cancelText='戻る'
            isOpen={isDeleteModalOpen}
            onConfirm={confirmDelete}
            onClose={cancelDelete}
            isDanger={true}
          />
        </div>
      </main>
      {isDragging && (
        <div className='pointer-events-none fixed inset-0 z-[1100] flex items-center justify-center bg-black/30'>
          <div className='flex items-center gap-2 rounded-md bg-white px-6 py-4 shadow-lg'>
            <img
              src={IconImage}
              alt='ファイルアップロード用の画像アイコン'
              className='size-5'
            />
            <p className='text-sm font-semibold'>ファイルをアップロードする</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticleEditorForm;
